import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  FLEX_AMO_SYNC_STATUS_LABELS_RU,
  FLEX_ANNOUNCED_KIND_LABELS_RU,
  FLEX_LOGISTICS_STATUSES,
  FLEX_STATUSES,
  FLEX_STATUS_LABELS_RU,
  FLEX_TARIFF_CATEGORY_LABELS_RU,
  flexRoundGelFinal,
  flexSupportsShipmentType,
  flexVolumetricWeightKg,
  getCountry,
  type FlexAnnouncedKind,
  type FlexPlace,
  type FlexStatus,
  type FlexTariffCategory,
} from "@mnp/shared";
import { Icon } from "../../components/Icon";
import { shortId, formatDateTime, telegramHref } from "../../lib/orders";
import { api, apiUrl } from "../../lib/api";
import { useOptionalRole } from "../../lib/role.tsx";
import { hasPermission } from "@mnp/shared";
import { PaymentModal } from "../reception/delivery/PaymentModal";
import {
  useFlexAnnounceTotal,
  useFlexCarrier,
  useFlexCarrierPayments,
  useFlexPayment,
  useFlexApprove,
  useFlexOrder,
  useFlexOrderAttachments,
  useFlexOrderUpdate,
  useFlexQuote,
  useFlexReject,
  useFlexStatusTransition,
  type FlexCarrierPayment,
  type FlexOrderAttachment,
  type FlexOrderDetail,
  type FlexOrderUpdatePatch,
} from "../../lib/useFlexOrders";

const FLEX_STATUS_BADGE: Record<FlexStatus, string> = {
  draft: "gray",
  submitted: "blue",
  in_review: "orange",
  approved: "teal",
  pickup: "yellow",
  preparing: "teal",
  customs: "purple",
  in_transit: "blue",
  assigned: "teal",
  delivery: "teal",
  delivered: "green",
  rejected: "red",
};

// Pre-review статусы — в этих фазах прячем «Итого» и поднимаем фото+описание
// наверх drawer body (см. план review-цикла 2026-05-29).
const FLEX_PRE_REVIEW_STATUSES: ReadonlySet<FlexStatus> = new Set([
  "submitted",
  "in_review",
  "rejected",
]);

// Flag emoji table mirrors apps/client/components/flex-form/FlexFormAtoms.tsx.
// Duplicated locally on purpose: extracting to @mnp/shared is a post-launch
// refactor (PRD §0 freeze). When that happens, replace this with the shared
// helper everywhere.
const FLAG_BY_CODE: Record<string, string> = {
  GE: "🇬🇪", RU: "🇷🇺", BY: "🇧🇾", AM: "🇦🇲", KZ: "🇰🇿",
  UA: "🇺🇦", MD: "🇲🇩", TR: "🇹🇷", UK: "🇬🇧", DE: "🇩🇪", FR: "🇫🇷",
  IT: "🇮🇹", ES: "🇪🇸", PL: "🇵🇱", NL: "🇳🇱", BE: "🇧🇪", AT: "🇦🇹",
  PT: "🇵🇹", IE: "🇮🇪", CZ: "🇨🇿", SK: "🇸🇰", HU: "🇭🇺", RO: "🇷🇴",
  BG: "🇧🇬", GR: "🇬🇷", HR: "🇭🇷", RS: "🇷🇸", BA: "🇧🇦", MK: "🇲🇰",
  AL: "🇦🇱", ME: "🇲🇪", XK: "🇽🇰", SI: "🇸🇮", NO: "🇳🇴", CH: "🇨🇭",
  IS: "🇮🇸", SE: "🇸🇪", FI: "🇫🇮", DK: "🇩🇰", EE: "🇪🇪", LV: "🇱🇻",
  LT: "🇱🇹", US: "🇺🇸", CA: "🇨🇦",
};

function flagFor(code?: string | null): string {
  if (!code) return "🏳️";
  return FLAG_BY_CODE[code] ?? "🏳️";
}

function countryLabel(code?: string | null): string {
  if (!code) return "—";
  const c = getCountry(code);
  return c ? `${flagFor(code)} ${c.nameRu}` : `${flagFor(code)} ${code}`;
}

function fullPhone(code?: string | null, local?: string | null): string {
  if (!code && !local) return "—";
  return [code, local].filter(Boolean).join(" ").trim() || "—";
}

function joinAddr(parts: Array<string | null | undefined>): string {
  return parts.filter((p) => p && String(p).trim()).join(", ") || "—";
}

function neighborFlexStatus(
  status: FlexStatus,
  direction: 1 | -1,
): FlexStatus | null {
  const idx = FLEX_STATUSES.indexOf(status);
  if (idx < 0) return null;
  const nextIdx = idx + direction;
  if (nextIdx < 0 || nextIdx >= FLEX_STATUSES.length) return null;
  return FLEX_STATUSES[nextIdx] ?? null;
}

// Admin-editable patch form. The admin can correct ANY field of a filled
// order. Values are kept as strings in the form (bools as "true"/""), then
// coerced to the PATCH payload on save per the field's kind.
type FieldKind =
  | "text"
  | "num" // float, nonneg
  | "intpos" // positive int
  | "int" // any int (e.g. floor)
  | "intnn" // nonneg int (e.g. crate count)
  | "bool"
  | "trielev" // boolean | null (есть/нет/неизвестно)
  | "customs";

// `satisfies` привязывает ключи словаря к PATCH-схеме сервера (@mnp/shared
// wire.ts): опечатка или переименованное на сервере поле перестают
// компилироваться. Обход через `as` страхует рантайм-тест «ключи FIELD_KINDS ⊆
// ключей updateFlexOrderSchema.shape» (FlexBody.formContract.test.ts).
// Exported for the contract test.
export const FIELD_KINDS = {
  // admin / processing — weightKg («Предв. вес») is edited in the «Итого»
  // panel, not here, so it's intentionally NOT in this form.
  declaredValueEur: "num", contents: "text", preorder: "bool",
  customsRiskLevel: "customs", pickupAddress: "text", deliveryAddress: "text",
  dimensionsLengthCm: "intpos", dimensionsWidthCm: "intpos", dimensionsHeightCm: "intpos",
  // route
  originCountry: "text", destinationCountry: "text",
  // sender — senderPassport intentionally NOT here: it's PII (audited reveal),
  // handled out-of-band via passportRevealed / passportEdited state so the
  // generic emptyForm/formToPatch path can't accidentally read or wipe it.
  senderFullName: "text", senderPhoneCode: "text", senderPhoneLocal: "text",
  senderAddressLine: "text",
  // recipient
  recipientFullName: "text", recipientCountry: "text", recipientCity: "text",
  recipientMissingPhone: "bool", recipientMissingAddress: "bool",
  recipientPhoneCode: "text", recipientPhoneLocal: "text", recipientMessenger: "text",
  recipientApartment: "text", recipientBuilding: "text", recipientStreet: "text",
  recipientZip: "text", recipientEmail: "text", recipientGeoUrl: "text",
  // pickup
  selfDeliver: "bool", pickupGeoUrl: "text", pickupCity: "text", pickupFloor: "int",
  pickupHasElevator: "trielev", pickupPhoneLocal: "text", pickupSameAsSender: "bool",
  pickupDay: "text", pickupSlot: "text",
  // parcel / crate / storage
  pieces: "intpos", crateNeeded: "bool", crateCount: "intnn", storageNeeded: "bool",
  storageUntil: "text", flexContentDescription: "text",
} satisfies Partial<Record<keyof FlexOrderUpdatePatch, FieldKind>>;

type FlexEditableField = keyof typeof FIELD_KINDS;
// Object.entries теряет литеральные ключи — фиксируем их одним кастом здесь,
// чтобы emptyForm/formToPatch читали поля типизированно (без any).
const FIELD_KIND_ENTRIES = Object.entries(FIELD_KINDS) as Array<
  [FlexEditableField, FieldKind]
>;

type EditFormState = Record<string, string>;

// Exported for the contract test (FlexBody.formContract.test.ts).
export function emptyForm(o: FlexOrderDetail | undefined): EditFormState {
  const f: EditFormState = {};
  for (const [field, kind] of FIELD_KIND_ENTRIES) {
    const v = o?.[field];
    if (kind === "bool") f[field] = v ? "true" : "";
    else if (kind === "trielev") f[field] = v === true ? "true" : v === false ? "false" : "";
    else if (kind === "customs") f[field] = v === "red" || v === "yellow" || v === "green" ? v : "";
    else f[field] = v == null ? "" : String(v);
  }
  return f;
}

// Convert form strings to a DELTA PATCH payload: only fields whose form string
// differs from the `baseline` snapshot (captured by emptyForm(data) when the
// admin opened edit mode) are emitted. An untouched form yields {} — a field
// the server never returned can no longer be silently wiped to null on save
// (карточка flex-wire-contract-shared, шаг 4). Семантика:
//   • было значение → админ очистил ("")  → поле уходит как null (очистка);
//   • поле не пришло с сервера и не тронуто → в PATCH не попадает вовсе;
//   • значение изменено → уходит коэрцированное значение.
// Empty strings clear nullable fields; invalid numbers are dropped (undefined)
// so a partial-typed field never blows up the save.
// Exported for the contract test.
export function formToPatch(f: EditFormState, baseline: EditFormState): FlexOrderUpdatePatch {
  const str = (s: string): string | null => (s.trim() === "" ? null : s.trim());
  const num = (s: string): number | null | undefined => {
    if (s.trim() === "") return null;
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  };
  const intpos = (s: string): number | null | undefined => {
    if (s.trim() === "") return null;
    const n = Math.floor(Number(s));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };
  const intAny = (s: string): number | null | undefined => {
    if (s.trim() === "") return null;
    const n = Math.trunc(Number(s));
    return Number.isFinite(n) ? n : undefined;
  };
  const intnn = (s: string): number | undefined => {
    if (s.trim() === "") return 0;
    const n = Math.floor(Number(s));
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  };
  const patch: Partial<Record<FlexEditableField, unknown>> = {};
  for (const [field, kind] of FIELD_KIND_ENTRIES) {
    const s = f[field] ?? "";
    // Дельта: значение в форме не отличается от снапшота → поле не трогали
    // (или вернули как было) — не шлём. Сравнение по строковому представлению
    // формы, т.е. ровно по тому, что видит и правит человек.
    if (s === (baseline[field] ?? "")) continue;
    if (kind === "text") patch[field] = str(s);
    else if (kind === "num") patch[field] = num(s);
    else if (kind === "intpos") patch[field] = intpos(s);
    else if (kind === "int") patch[field] = intAny(s);
    else if (kind === "intnn") patch[field] = intnn(s);
    else if (kind === "bool") patch[field] = s === "true";
    else if (kind === "trielev") patch[field] = s === "" ? null : s === "true";
    else if (kind === "customs") patch[field] = s === "" ? null : s;
  }
  // Drop fields whose coercion failed (undefined) so they're left untouched.
  for (const k of Object.keys(patch) as FlexEditableField[]) {
    if (patch[k] === undefined) delete patch[k];
  }
  return patch as FlexOrderUpdatePatch;
}

function fmtDimensions(
  l?: number | null,
  w?: number | null,
  h?: number | null,
): string {
  const parts = [l, w, h].map((n) => (n == null ? "—" : String(n)));
  if (parts.every((p) => p === "—")) return "—";
  return `${parts.join(" × ")} см`;
}

// ── Per-place parcel parameters (FlexPlace[]) ──────────────────────────────
// Edited as strings (mirrors the rest of the form), coerced to FlexPlace on
// save. One row per place: вес, выбор коробки ИЛИ ручные габариты (для
// негабарита), обрешётка-флаг, негабарит-флаг (ручной), объёмный вес.
type PlaceRow = {
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  crateNeeded: boolean;
  oversized: boolean;
  comment: string;
};

function emptyPlaceRow(): PlaceRow {
  return {
    weightKg: "",
    lengthCm: "",
    widthCm: "",
    heightCm: "",
    crateNeeded: false,
    oversized: false,
    comment: "",
  };
}

const numToStr = (n: number | null | undefined): string => (n == null ? "" : String(n));

function placeToRow(p: FlexPlace): PlaceRow {
  return {
    weightKg: numToStr(p.weightKg),
    lengthCm: numToStr(p.lengthCm),
    widthCm: numToStr(p.widthCm),
    heightCm: numToStr(p.heightCm),
    crateNeeded: !!p.crateNeeded,
    oversized: !!p.oversized,
    comment: p.comment ?? "",
  };
}

const parseNonNeg = (s: string): number | null => {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
};
const parsePosInt = (s: string): number | null => {
  if (s.trim() === "") return null;
  const n = Math.floor(Number(s));
  return Number.isFinite(n) && n > 0 ? n : null;
};

function rowToPlace(r: PlaceRow, additional = false): FlexPlace {
  return {
    weightKg: parseNonNeg(r.weightKg),
    lengthCm: parsePosInt(r.lengthCm),
    widthCm: parsePosInt(r.widthCm),
    heightCm: parsePosInt(r.heightCm),
    crateNeeded: r.crateNeeded,
    // Обрешётка implies negabarit; otherwise it's the manual flag.
    oversized: r.oversized || r.crateNeeded,
    additional,
    comment: r.comment.trim() === "" ? null : r.comment.trim(),
  };
}

// Seed the editor from saved places, split into base places and «довоз» ones.
// If nothing is saved yet, open `pieces` empty base rows so the admin fills
// "сколько мест, столько и параметров". If довоз is on but no extra places are
// saved, open `additionalPlacesCount` empty довоз rows.
function seedPlaces(o: FlexOrderDetail | undefined): { base: PlaceRow[]; addl: PlaceRow[] } {
  const raw: FlexPlace[] = Array.isArray(o?.placesJson) ? o.placesJson : [];
  const addlCount = o?.additionalPlacesCount ?? 0;
  if (raw.length > 0) {
    const base = raw.filter((p) => !p?.additional).map(placeToRow);
    let addl = raw.filter((p) => p?.additional).map(placeToRow);
    if (addl.length === 0 && o?.additionalDelivery && addlCount > 0) {
      addl = Array.from({ length: Math.min(addlCount, 50) }, emptyPlaceRow);
    }
    return { base, addl };
  }
  const n = typeof o?.pieces === "number" && o.pieces > 0 ? Math.min(o.pieces, 50) : 0;
  const addl =
    o?.additionalDelivery && addlCount > 0
      ? Array.from({ length: Math.min(addlCount, 50) }, emptyPlaceRow)
      : [];
  return { base: Array.from({ length: n }, emptyPlaceRow), addl };
}

function fmtVol(l: number | null, w: number | null, h: number | null): string {
  const v = flexVolumetricWeightKg(l, w, h);
  return v == null ? "—" : `${v.toFixed(2)} кг`;
}

function fmtPlace(p: FlexPlace): string {
  const w = p.weightKg == null ? "вес —" : `${p.weightKg} кг`;
  const dims = fmtDimensions(p.lengthCm, p.widthCm, p.heightCm);
  const parts = [`${w} · ${dims}`, `объёмный ${fmtVol(p.lengthCm, p.widthCm, p.heightCm)}`];
  if (p.crateNeeded) parts.push("обрешётка");
  if (p.oversized) parts.push("негабаритный груз");
  if (p.comment && p.comment.trim()) parts.push(p.comment.trim());
  return parts.join(" · ");
}

// One editable place card: вес, ручные габариты (необязательные), обрешётка-флаг
// (включает негабарит и делает габариты обязательными), негабарит-флаг (ручной),
// объёмный вес. `label` distinguishes Место vs Довоз rows.
function PlaceRowEditor({
  index,
  testKey,
  label,
  row,
  onChange,
  onRemove,
}: {
  index: number;
  testKey: string;
  label: string;
  row: PlaceRow;
  onChange: (patch: Partial<PlaceRow>) => void;
  onRemove: () => void;
}) {
  const numInput = (
    key: keyof PlaceRow,
    placeholder: string,
    width: number,
    step: string,
  ) => (
    <input
      type="number"
      min={step === "1" ? "1" : "0"}
      step={step}
      value={row[key] as string}
      onChange={(e) => onChange({ [key]: e.target.value } as Partial<PlaceRow>)}
      placeholder={placeholder}
      data-testid={`${testKey}-${index}-${key}`}
      style={{ width }}
    />
  );

  const vl = parsePosInt(row.lengthCm);
  const vw = parsePosInt(row.widthCm);
  const vh = parsePosInt(row.heightCm);
  // Обрешётка implies negabarit and makes dimensions mandatory.
  const negabarit = row.oversized || row.crateNeeded;

  return (
    <div
      data-testid={`${testKey}-row-${index}`}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        border: "1px solid var(--mnp-border, #e5e7eb)",
        borderRadius: 8,
      }}
    >
      <span className="bold" style={{ minWidth: 84 }}>
        {label} {index + 1}
      </span>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        вес
        {numInput("weightKg", "кг", 80, "0.001")}
      </label>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        Д×Ш×В
        {numInput("lengthCm", "Д", 56, "1")}
        {numInput("widthCm", "Ш", 56, "1")}
        {numInput("heightCm", "В", 56, "1")}
        {row.crateNeeded && (
          <span className="muted" style={{ fontSize: 11 }}>
            обязательно при обрешётке
          </span>
        )}
      </span>
      <label
        style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
        title={row.crateNeeded ? "Обрешётка всегда негабаритная" : undefined}
      >
        <input
          type="checkbox"
          checked={negabarit}
          disabled={row.crateNeeded}
          onChange={(e) => onChange({ oversized: e.target.checked })}
          data-testid={`${testKey}-${index}-oversized`}
        />
        негабаритный груз
      </label>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <input
          type="checkbox"
          checked={row.crateNeeded}
          onChange={(e) => onChange({ crateNeeded: e.target.checked })}
          data-testid={`${testKey}-${index}-crate`}
        />
        обрешётка
      </label>
      <span className="muted" style={{ fontSize: 11 }}>
        объёмный вес: {fmtVol(vl, vw, vh)}
      </span>
      <button
        type="button"
        className="btn-admin ghost"
        onClick={onRemove}
        data-testid={`${testKey}-${index}-remove`}
        style={{ marginLeft: "auto" }}
      >
        Удалить
      </button>
      <input
        type="text"
        value={row.comment}
        onChange={(e) => onChange({ comment: e.target.value })}
        placeholder="Внутренний комментарий (необязательно)"
        data-testid={`${testKey}-${index}-comment`}
        style={{ flexBasis: "100%", width: "100%" }}
      />
    </div>
  );
}

// Reusable «Места (параметры по-местно)» editor: base places + add button +
// «Довоз» toggle/count/rows. Used both in full edit mode («Править») and in the
// highlighted block under «Итого» when the admin announces the FINAL sum.
function PlacesEditor({
  baseRows,
  addlRows,
  additionalDelivery,
  setBase,
  addBase,
  removeBase,
  setAddl,
  removeAddl,
  toggleAdditional,
  setAddlCount,
}: {
  baseRows: PlaceRow[];
  addlRows: PlaceRow[];
  additionalDelivery: boolean;
  setBase: (i: number, patch: Partial<PlaceRow>) => void;
  addBase: () => void;
  removeBase: (i: number) => void;
  setAddl: (i: number, patch: Partial<PlaceRow>) => void;
  removeAddl: (i: number) => void;
  toggleAdditional: (on: boolean) => void;
  setAddlCount: (n: number) => void;
}) {
  return (
    <div
      data-testid="flex-places-editor"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      {baseRows.length === 0 && (
        <div className="muted" style={{ fontSize: 13 }}>
          Мест нет. Добавьте место, чтобы внести вес, габариты и обрешётку.
        </div>
      )}
      {baseRows.map((r, i) => (
        <PlaceRowEditor
          key={i}
          index={i}
          testKey="flex-place"
          label="Место"
          row={r}
          onChange={(patch) => setBase(i, patch)}
          onRemove={() => removeBase(i)}
        />
      ))}
      <button
        type="button"
        className="btn-admin"
        onClick={addBase}
        data-testid="flex-place-add"
        style={{ alignSelf: "flex-start" }}
      >
        + Добавить место
      </button>

      {/* «Довоз»: клиент дослал ещё места. Пока они не заполнены,
          заказ не двигается вперёд по статусу (гард на бэке). */}
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 4 }}>
        <input
          type="checkbox"
          checked={additionalDelivery}
          onChange={(e) => toggleAdditional(e.target.checked)}
          data-testid="flex-additional-toggle"
        />
        Довоз (клиент дослал ещё места)
      </label>
      {additionalDelivery && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Кол-во доп. мест
            <input
              type="number"
              min="0"
              max="50"
              step="1"
              value={addlRows.length}
              onChange={(e) => setAddlCount(Number(e.target.value))}
              data-testid="flex-additional-count"
              style={{ width: 72 }}
            />
          </label>
          {addlRows.map((r, i) => (
            <PlaceRowEditor
              key={i}
              index={i}
              testKey="flex-addl"
              label="Довоз"
              row={r}
              onChange={(patch) => setAddl(i, patch)}
              onRemove={() => removeAddl(i)}
            />
          ))}
          <div className="muted" style={{ fontSize: 11 }}>
            Пока места довоза не заполнены, заказ нельзя двигать вперёд по статусу.
          </div>
        </div>
      )}
    </div>
  );
}

const FLEX_QUOTE_REASON_RU: Record<string, string> = {
  commercial: "Коммерция — тариф уточнить вручную",
  serbia_other: "Сербия (др. город) — уточнить",
  unknown_country: "страна не в тарифе — уточнить",
  no_weight: "нет общего веса",
  not_found: "—",
};

// «Итого» panel — pinned to the top of the deal, outside edit mode. Toggle
// Личные вещи / Коммерция, the preliminary sum is auto-calculated from the
// общий вес + tariff (in € and ₾), the admin reviews, marks предварительная
// (yellow) / итоговая (green) and presses «Объявить» → persists + notifies the
// client. Dimensions/weight live in «Править»; this panel is always editable.
function FlexTotalPanel({
  orderId,
  weightKg,
  updatedAt,
  tariffCategory,
  announcedAmountEur,
  announcedAmountGel,
  announcedKind,
  announcedAt,
  paidAt,
  paidAmountGel,
  onPaid,
  // Тип отправки (Личные вещи/Коммерция) показываем только для маршрутов,
  // где это подразумевает таможня (РФ/Казахстан/Беларусь). Для актуального
  // коридора Грузия→Европа тумблер скрыт, тариф всегда «Личные вещи».
  showShipmentType,
  // «Итого» kind поднят в FlexBody, чтобы под панелью можно было показать
  // блок «Места» именно при выборе «Итоговая» (см. Task A).
  kind,
  onKindChange,
  // Меняется, когда из блока «Места» нажали «Сохранить места и посчитать» —
  // панель пересчитывает итоговые €/₾ по свежему quote.
  recomputeFinalKey = 0,
  // Read-only роль (нет orders:status): суммы/статус оплаты видны, но все
  // мутационные контролы скрыты/выключены — иначе кнопки дают 403.
  readOnly = false,
}: {
  orderId: string;
  weightKg?: string | null;
  updatedAt?: string | null;
  tariffCategory?: "personal" | "commercial" | null;
  announcedAmountEur?: string | null;
  announcedAmountGel?: string | null;
  announcedKind?: "preliminary" | "final" | null;
  announcedAt?: string | null;
  paidAt?: string | null;
  paidAmountGel?: string | null;
  onPaid?: () => void;
  showShipmentType: boolean;
  kind: FlexAnnouncedKind;
  onKindChange: (k: FlexAnnouncedKind) => void;
  recomputeFinalKey?: number;
  readOnly?: boolean;
}) {
  const announce = useFlexAnnounceTotal(orderId);
  const update = useFlexOrderUpdate(orderId);
  const payment = useFlexPayment(orderId);
  const [payOpen, setPayOpen] = useState(false);
  // Ключ идемпотентности платежа: новый на каждое открытие формы. Ретрай того
  // же сабмита (даблклик/обрыв сети) уходит с тем же ключом — сервер не
  // задваивает paidAmountGel и кассовую операцию.
  const payIdemKey = useRef<string>("");
  // Сервер ответил alreadyRecorded (ретрай уже учтённого платежа) — no-op
  // нельзя показывать как тихий успех: оператор должен видеть, что повтор
  // НЕ записан. Сбрасывается при следующем открытии формы оплаты.
  const [payNotice, setPayNotice] = useState<string | null>(null);
  // Когда маршрут не подразумевает тип отправки — тариф всегда «Личные вещи».
  const category: FlexTariffCategory = showShipmentType
    ? tariffCategory === "commercial"
      ? "commercial"
      : "personal"
    : "personal";
  const quoteQ = useFlexQuote(orderId, { updatedAt, enabled: category === "personal" });
  const quote = category === "personal" ? quoteQ.data : undefined;

  const [eurStr, setEurStr] = useState("");
  const [gelStr, setGelStr] = useState("");
  const [prelimWeightStr, setPrelimWeightStr] = useState(weightKg ?? "");

  useEffect(() => {
    setPrelimWeightStr(weightKg ?? "");
  }, [weightKg]);

  // The quote block matching the selected kind: preliminary uses the manual
  // «Предв. вес»; final uses Σ max(actual, volumetric) over places.
  const block = kind === "preliminary" ? quote?.preliminary : quote?.final;
  const rate = quote?.rate ?? null;
  const gelExtra = block?.ok ? block.gelExtra ?? 0 : 0;

  // € → ₾ conversion (client-side, live). The calc always rounds the lari UP to
  // 5 — for both предварительная and итоговая.
  const eurToGel = (eur: number): number | null => {
    if (rate == null || !Number.isFinite(eur)) return null;
    return flexRoundGelFinal(eur * rate + gelExtra);
  };
  const blockGel = block?.ok && block.eurTotal != null ? eurToGel(block.eurTotal) : null;

  // Seed the fields from the last announced value (so a saved deal reopens with
  // its announced price); «Посчитать» overwrites them with the fresh auto-calc,
  // manual edits are otherwise free. We do NOT auto-recompute on every change.
  const announcedSig = `${announcedAmountEur ?? ""}|${announcedAmountGel ?? ""}|${announcedKind ?? ""}`;
  const lastAnnounced = useRef<string | null>(null);
  useEffect(() => {
    if (lastAnnounced.current === announcedSig) return;
    lastAnnounced.current = announcedSig;
    if (announcedAmountGel != null) {
      setEurStr(announcedAmountEur ?? "");
      setGelStr(announcedAmountGel);
      if (announcedKind) onKindChange(announcedKind);
    }
  }, [announcedSig, announcedAmountEur, announcedAmountGel, announcedKind, onKindChange]);

  // «Посчитать» — overwrite the € / ₾ fields with the auto-calc for the chosen
  // kind. The only path that auto-writes the fields.
  function recompute() {
    if (!block?.ok || block.eurTotal == null) return;
    setEurStr(String(block.eurTotal));
    const g = eurToGel(block.eurTotal);
    setGelStr(g != null ? String(g) : "");
  }

  // «Сохранить места и посчитать» из блока под панелью бампает recomputeFinalKey.
  // Места только что записались, quote перезапрашивается по новому updatedAt —
  // поэтому ждём, пока итоговый блок станет ok, и тогда подставляем расчёт в поля.
  const lastRecomputeKey = useRef(recomputeFinalKey);
  const recomputePending = useRef(false);
  useEffect(() => {
    if (recomputeFinalKey !== lastRecomputeKey.current) {
      lastRecomputeKey.current = recomputeFinalKey;
      recomputePending.current = true;
    }
    if (recomputePending.current && block?.ok && block.eurTotal != null) {
      recomputePending.current = false;
      recompute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recomputeFinalKey, block]);

  // € drives ₾ (recompute live); editing ₾ never changes €.
  function onEurChange(v: string) {
    setEurStr(v);
    if (v.trim() === "") {
      setGelStr("");
      return;
    }
    const g = eurToGel(Number(v));
    if (g != null) setGelStr(String(g));
  }

  function savePrelimWeight() {
    const v = prelimWeightStr.trim();
    const n = v === "" ? null : Number(v);
    const cur = weightKg == null || weightKg === "" ? null : Number(weightKg);
    if (n === cur) return;
    // Схема (shared wire): weightKg positive().nullable() — 0 отвергается
    // сервером 400-кой без индикации; не шлём его вовсе (пусто = очистка).
    if (n === null || (Number.isFinite(n) && n > 0)) update.mutate({ weightKg: n });
  }

  const hasPrelimWeight = prelimWeightStr.trim() !== "" && Number(prelimWeightStr) > 0;
  const gelNum = Number(gelStr);
  const eurNum = Number(eurStr);
  const gelValid = gelStr.trim() !== "" && Number.isFinite(gelNum) && gelNum > 0;
  const eurOk = eurStr.trim() === "" || (Number.isFinite(eurNum) && eurNum > 0);
  // «Объявить» показываем «нажатой» (Объявлено ✓), когда поля €/₾ численно
  // совпадают с уже объявленной суммой ТОГО ЖЕ вида. Меняются вводные (€/₾/вес/
  // места) → кнопка снова активна. Иначе непонятно, объявлено или нет.
  const isThisKindAnnounced = announcedKind === kind && announcedAmountGel != null;
  const sameGel = Number(gelStr) === Number(announcedAmountGel);
  const sameEur =
    eurStr.trim() === ""
      ? announcedAmountEur == null || announcedAmountEur === ""
      : Number(eurStr) === Number(announcedAmountEur);
  const alreadyAnnounced = isThisKindAnnounced && sameGel && sameEur;
  // Авто-расчёт разошёлся с объявленным (поменяли вес/места/перевозчика) —
  // подсказываем пересчитать и объявить заново.
  const blockDiverged =
    isThisKindAnnounced && blockGel != null && Number(blockGel) !== Number(announcedAmountGel);
  const accent = kind === "final" ? "var(--mnp-green, #15803d)" : "var(--mnp-warn, #b45309)";
  const finalWeight = quote?.finalWeightKg;
  // Дистанционная оплата (вкл. ЧАСТИЧНУЮ): доступна с момента объявления ЛЮБОЙ
  // суммы (предв. или итог), пока заявка не оплачена полностью. Копит сумму на
  // сервере; полная оплата (≥ итог) сама проставит «оплачено».
  const paid = paidAt != null;
  const announcedNum = announcedAmountGel != null ? Number(announcedAmountGel) : 0;
  const paidNum = paidAmountGel != null ? Number(paidAmountGel) : 0;
  const canPay = announcedAmountGel != null && announcedNum > 0;
  const remainingGel = Math.max(0, Math.round((announcedNum - paidNum) * 100) / 100);

  return (
    <div
      data-testid="flex-total-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "12px 14px",
        marginBottom: 12,
        borderRadius: 10,
        border: `2px solid ${accent}`,
        background: "var(--mnp-bg-alt, #fff)",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <span className="bold" style={{ fontSize: 14 }}>
          Итого
        </span>
        {showShipmentType && (
          <span style={{ display: "inline-flex", gap: 10 }}>
            {(["personal", "commercial"] as const).map((c) => (
              <label key={c} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <input
                  type="radio"
                  name={`flex-tariff-${orderId}`}
                  checked={category === c}
                  disabled={readOnly}
                  onChange={() => {
                    if (c !== category) update.mutate({ tariffCategory: c });
                  }}
                  data-testid={`flex-tariff-${c}`}
                />
                {FLEX_TARIFF_CATEGORY_LABELS_RU[c]}
              </label>
            ))}
          </span>
        )}
      </div>

      {/* Weights: предв. (manual, drives preliminary) + итог. (Σ max по местам). */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          Предв. вес
          <input
            type="number"
            min="0"
            step="0.001"
            value={prelimWeightStr}
            disabled={readOnly}
            onChange={(e) => setPrelimWeightStr(e.target.value)}
            onBlur={savePrelimWeight}
            placeholder="кг"
            data-testid="flex-total-prelim-weight"
            style={{
              width: 110,
              fontSize: 20,
              fontWeight: 700,
              padding: "4px 8px",
              border: hasPrelimWeight
                ? "1px solid var(--mnp-border, #e5e7eb)"
                : "2px solid var(--mnp-red, #c00)",
            }}
          />
          кг
          {update.isError && (
            <span style={{ color: "var(--mnp-red, #c00)", fontSize: 12, marginLeft: 8 }}>
              Не сохранилось — проверьте значение
            </span>
          )}
        </label>
        <span className="muted" style={{ fontSize: 12 }}>
          Итог. вес (из мест): {finalWeight != null ? `${finalWeight} кг` : "—"}
        </span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <input
            type="number"
            min="0"
            step="0.01"
            value={eurStr}
            disabled={readOnly}
            onChange={(e) => onEurChange(e.target.value)}
            placeholder="0.00"
            data-testid="flex-total-eur"
            style={{ width: 110, fontSize: 18, fontWeight: 700, padding: "4px 8px" }}
          />
          €
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <input
            type="number"
            min="0"
            step="0.01"
            value={gelStr}
            disabled={readOnly}
            onChange={(e) => setGelStr(e.target.value)}
            placeholder="0.00"
            data-testid="flex-total-amount"
            style={{ width: 130, fontSize: 22, fontWeight: 700, padding: "4px 8px" }}
          />
          ₾
        </label>
        {!readOnly && (
          <button
            type="button"
            className="btn-admin"
            disabled={!block?.ok}
            onClick={recompute}
            data-testid="flex-total-recompute"
            title="Подставить расчёт по тарифу в поля"
          >
            Посчитать
          </button>
        )}
        {(["preliminary", "final"] as const).map((k) => (
          <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <input
              type="radio"
              name={`flex-total-kind-${orderId}`}
              checked={kind === k}
              disabled={readOnly}
              onChange={() => onKindChange(k)}
              data-testid={`flex-total-kind-${k}`}
            />
            {FLEX_ANNOUNCED_KIND_LABELS_RU[k]}
          </label>
        ))}
        {!readOnly && (
          <button
            type="button"
            className="btn-admin primary"
            disabled={!gelValid || !eurOk || announce.isPending || alreadyAnnounced}
            onClick={() =>
              announce.mutate({
                amountGel: gelNum,
                amountEur: eurStr.trim() === "" ? undefined : eurNum,
                kind,
              })
            }
            data-testid="flex-total-announce"
          >
            {announce.isPending ? "Отправка…" : alreadyAnnounced ? "Объявлено ✓" : "Объявить"}
          </button>
        )}
      </div>

      <div style={{ fontSize: 12, lineHeight: 1.5 }}>
        {block?.ok && (
          <span className="muted">
            Расчёт ({FLEX_ANNOUNCED_KIND_LABELS_RU[kind].toLowerCase()}): {block.eurTotal} € ·{" "}
            {blockGel ?? "—"} ₾{quote?.rate ? ` · курс ${quote.rate}` : " · курс недоступен"}
          </span>
        )}
        {category === "personal" && block && !block.ok && (
          <span style={block.reason === "no_weight" ? { color: "var(--mnp-red, #c00)", fontWeight: 600 } : undefined} className={block.reason === "no_weight" ? undefined : "muted"}>
            {block.reason === "no_weight"
              ? kind === "preliminary"
                ? "Укажите предв. вес для расчёта."
                : "Заполните места (вес/габариты) для итогового расчёта."
              : `Авто-тариф не применён: ${FLEX_QUOTE_REASON_RU[block.reason ?? ""] ?? "уточнить"}. Введите € или ₾ вручную.`}
          </span>
        )}
        {category === "commercial" && (
          <span className="muted">Коммерция — тариф уточнить вручную.</span>
        )}
        {announcedAmountGel != null && announcedKind && (
          <span style={{ color: accent, fontWeight: 600, marginLeft: 8 }}>
            Объявлено: {announcedAmountEur != null ? `${announcedAmountEur} € / ` : ""}
            {announcedAmountGel} ₾ · {FLEX_ANNOUNCED_KIND_LABELS_RU[announcedKind].toLowerCase()}
            {announcedAt ? ` · ${formatDateTime(announcedAt)}` : ""}
          </span>
        )}
        {blockDiverged && (
          <div
            style={{ color: accent, fontWeight: 600, marginTop: 4 }}
            data-testid="flex-total-diverged"
          >
            Вводные изменились — нажмите «Посчитать» и объявите заново.
          </div>
        )}
        {announce.isError && (
          <span style={{ color: "var(--mnp-red, #c00)", marginLeft: 8 }}>Не удалось объявить</span>
        )}
      </div>

      {/* Дистанционная оплата (частичная/полная) через эндпоинт /payment. */}
      {paid ? (
        <div style={{ fontSize: 13 }}>
          <span style={{ color: "var(--mnp-green, #15803d)", fontWeight: 700 }}>✓ Оплачено</span>
          {paidNum > 0 && <span className="muted" style={{ marginLeft: 8 }}>({paidNum} ₾)</span>}
        </div>
      ) : canPay && !readOnly ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn-admin primary"
            disabled={payment.isPending}
            onClick={() => {
              payIdemKey.current = crypto.randomUUID();
              setPayNotice(null);
              setPayOpen(true);
            }}
            data-testid="flex-mark-paid"
          >
            Оплатить
          </button>
          <span className="muted" style={{ fontSize: 11 }}>
            {paidNum > 0
              ? `принято ${paidNum} из ${announcedNum} ₾ · можно частично`
              : "дистанционная оплата (можно частично) — способ и сумма"}
          </span>
          {payment.isError && (
            <span style={{ color: "var(--mnp-red, #c00)", fontSize: 12 }} data-testid="flex-pay-error">
              {/* Текст сервера (напр. 409 idempotency_mismatch) важнее общей фразы. */}
              {(payment.error as { body?: { message?: string } } | null)?.body?.message ??
                "Не удалось провести оплату"}
            </span>
          )}
        </div>
      ) : null}

      {/* no-op ретрая: сервер ничего не записал — заметное предупреждение
          вместо тихого закрытия модалки (стиль — как у статус-баннеров выше). */}
      {payNotice && (
        <div
          style={{ color: "var(--mnp-warn, #b45309)", fontWeight: 600, fontSize: 13 }}
          data-testid="flex-pay-noop"
        >
          ⚠ {payNotice}
        </div>
      )}

      <PaymentModal
        open={payOpen}
        amountGel={remainingGel > 0 ? remainingGel : announcedNum}
        loading={payment.isPending}
        onClose={() => setPayOpen(false)}
        onSubmit={(p) =>
          payment.mutate(
            { amountGel: p.amountGel, method: p.method, idempotencyKey: payIdemKey.current },
            {
              onSuccess: (res) => {
                setPayOpen(false);
                // Ретрай уже учтённого платежа: сервер ничего не записал —
                // показываем предупреждение вместо тихого успеха.
                if (res?.alreadyRecorded) {
                  setPayNotice("Платёж уже был учтён ранее — повтор не записан");
                }
                onPaid?.();
              },
            },
          )
        }
      />
    </div>
  );
}

// Дата из сервера (pickupDate — ISO date-time; pickupDay клиента — ISO
// «YYYY-MM-DD») → «YYYY-MM-DD» для <input type="date">.
function isoDay(v: string | null | undefined): string {
  if (!v) return "";
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v));
  return m?.[1] ?? "";
}

// Забор · перевозчик (2026-06-05). Показывается только когда нужен курьер
// (selfDeliver=false) и заявка уже принята в работу. Админ помечает, кто
// забирает посылку (свободный идентификатор — юзернейм/телефон/иное), и может
// перенести дату забора (отправитель мог передоговориться). Сумму услуг
// перевозчика распределяем отдельно — в панели оплаты рядом с «Итого» (Phase B).
function FlexCarrierPanel({
  orderId,
  pickupCarrierRef,
  pickupDate,
  pickupDay,
  pickupSlot,
  // Read-only роль (нет orders:status): назначение/дата видны, контролы выключены.
  readOnly = false,
}: {
  orderId: string;
  pickupCarrierRef?: string | null;
  pickupDate?: string | null;
  pickupDay?: string | null;
  pickupSlot?: string | null;
  readOnly?: boolean;
}) {
  const carrier = useFlexCarrier(orderId);
  // Серверная дата забора: редактируемая pickupDate, иначе исходный день клиента.
  const serverDay = isoDay(pickupDate) || isoDay(pickupDay);
  const [ref, setRef] = useState(pickupCarrierRef ?? "");
  const [day, setDay] = useState(serverDay);
  const [saved, setSaved] = useState(false);

  // Подтягиваем серверные значения после рефетча (обновление заявки).
  useEffect(() => {
    setRef(pickupCarrierRef ?? "");
  }, [pickupCarrierRef]);
  useEffect(() => {
    setDay(serverDay);
  }, [serverDay]);

  const clientDay = isoDay(pickupDay);
  const dirty = ref.trim() !== (pickupCarrierRef ?? "").trim() || day !== serverDay;
  const rescheduled = !!clientDay && !!day && day !== clientDay;
  const assigned = !!(pickupCarrierRef && pickupCarrierRef.trim());
  const errMsg =
    (carrier.error as any)?.body?.message ??
    (carrier.error as any)?.message ??
    null;

  function save() {
    setSaved(false);
    carrier.mutate(
      {
        pickupCarrierRef: ref.trim() ? ref.trim() : null,
        pickupDate: day ? day : null,
      },
      { onSuccess: () => setSaved(true) },
    );
  }

  return (
    <div
      data-testid="flex-carrier-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "12px 14px",
        marginBottom: 12,
        borderRadius: 10,
        border: "2px solid var(--mnp-amber, #d97706)",
        background: "var(--mnp-bg-alt, #fff)",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
        <span className="bold" style={{ fontSize: 14 }}>
          Забор · перевозчик
        </span>
        <span
          className="muted"
          style={{
            fontSize: 11,
            padding: "1px 8px",
            borderRadius: 999,
            border: "1px solid var(--mnp-border, #e5e7eb)",
            color: assigned ? "var(--mnp-green, #15803d)" : undefined,
          }}
        >
          {assigned ? "назначен" : "не назначен"}
        </span>
      </div>

      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
        <span className="muted">Кто забирает (юзернейм / телефон / иное)</span>
        <input
          type="text"
          value={ref}
          disabled={readOnly}
          onChange={(e) => setRef(e.target.value)}
          placeholder="@username, +995…"
          data-testid="flex-carrier-ref"
          style={{ width: "100%" }}
        />
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
        <span className="muted">Дата забора</span>
        <input
          type="date"
          value={day}
          disabled={readOnly}
          onChange={(e) => setDay(e.target.value)}
          data-testid="flex-carrier-date"
          style={{ width: "100%", fontVariantNumeric: "tabular-nums" }}
        />
      </label>

      <p className="muted" style={{ fontSize: 11, margin: 0 }}>
        Желаемый клиентом: {clientDay || "—"}
        {pickupSlot ? ` · ${pickupSlot}` : ""}
        {rescheduled ? " · перенесён" : ""}
      </p>

      {!readOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={save}
            disabled={!dirty || carrier.isPending}
            data-testid="flex-carrier-save"
          >
            {carrier.isPending ? "…" : "Сохранить"}
          </button>
          {saved && !dirty && !carrier.isPending && (
            <span className="muted" style={{ fontSize: 12, color: "var(--mnp-green, #15803d)" }}>
              Сохранено
            </span>
          )}
        </div>
      )}

      {errMsg && (
        <p style={{ margin: 0, fontSize: 12, color: "var(--mnp-red, #dc2626)" }}>{errMsg}</p>
      )}
    </div>
  );
}

// Забор · оплата перевозчику (2026-06-05, Phase B). Рядом с «Итого»: админ
// задаёт согласованную сумму услуг перевозчика и распределяет её по способам
// оплаты — как в Кассе «Добавить метод оплаты» (часть наличными, часть
// переводом, можно остаться немного должны). Хранится в сделке, не в Кассе;
// долг = стоимость услуг − Σ оплат.
type CarrierPayRow = {
  id?: string;
  method: "cash" | "transfer";
  amountStr: string;
  comment: string;
  at?: string;
};

const CARRIER_PAY_METHOD_LABELS: Record<"cash" | "transfer", string> = {
  cash: "Наличные",
  transfer: "Перевод",
};

function rowsFromServer(payments?: FlexCarrierPayment[] | null): CarrierPayRow[] {
  if (!Array.isArray(payments)) return [];
  return payments.map((p) => ({
    id: p.id,
    method: p.method,
    amountStr: String(p.amountGel),
    comment: p.comment ?? "",
    at: p.at,
  }));
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function FlexCarrierPaymentPanel({
  orderId,
  carrierFeeGel,
  carrierPayments,
  // Read-only роль (нет orders:status): суммы/баланс видны, контролы выключены.
  readOnly = false,
}: {
  orderId: string;
  carrierFeeGel?: string | null;
  carrierPayments?: FlexCarrierPayment[] | null;
  readOnly?: boolean;
}) {
  const carrierPay = useFlexCarrierPayments(orderId);
  const [feeStr, setFeeStr] = useState(carrierFeeGel ?? "");
  const [rows, setRows] = useState<CarrierPayRow[]>(() => rowsFromServer(carrierPayments));
  const [saved, setSaved] = useState(false);

  // Подтягиваем серверные значения после рефетча.
  useEffect(() => {
    setFeeStr(carrierFeeGel ?? "");
  }, [carrierFeeGel]);
  useEffect(() => {
    setRows(rowsFromServer(carrierPayments));
  }, [carrierPayments]);

  const fee = Number.parseFloat(feeStr) || 0;
  const paid = rows.reduce((s, r) => s + (Number.parseFloat(r.amountStr) || 0), 0);
  const balance = round2(fee - paid); // >0 — долг перевозчику, <0 — переплата

  function touch() {
    setSaved(false);
  }
  function setRow(i: number, patch: Partial<CarrierPayRow>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    touch();
  }
  function addRow() {
    setRows((prev) => [...prev, { method: "cash", amountStr: "", comment: "" }]);
    touch();
  }
  function removeRow(i: number) {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
    touch();
  }

  function save() {
    setSaved(false);
    const payments = rows
      .map((r) => ({
        id: r.id,
        method: r.method,
        amountGel: Number.parseFloat(r.amountStr) || 0,
        comment: r.comment.trim() ? r.comment.trim() : null,
        at: r.at,
      }))
      .filter((p) => p.amountGel > 0);
    carrierPay.mutate(
      { carrierFeeGel: feeStr.trim() === "" ? null : fee, payments },
      { onSuccess: () => setSaved(true) },
    );
  }

  const errMsg =
    (carrierPay.error as any)?.body?.message ?? (carrierPay.error as any)?.message ?? null;

  const balanceLabel =
    balance > 0
      ? `Остаток (долг): ${balance} ₾`
      : balance < 0
        ? `Переплата: ${Math.abs(balance)} ₾`
        : fee > 0
          ? "Оплачено полностью"
          : "—";

  return (
    <div
      data-testid="flex-carrier-pay-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "12px 14px",
        marginBottom: 12,
        borderRadius: 10,
        border: "2px solid var(--mnp-amber, #d97706)",
        background: "var(--mnp-bg-alt, #fff)",
      }}
    >
      <span className="bold" style={{ fontSize: 14 }}>
        Забор · оплата перевозчику
      </span>

      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
        <span className="muted">Стоимость услуг перевозчика, ₾</span>
        <input
          type="number"
          inputMode="decimal"
          min={0}
          value={feeStr}
          disabled={readOnly}
          onChange={(e) => {
            setFeeStr(e.target.value);
            touch();
          }}
          placeholder="0"
          data-testid="flex-carrier-fee"
          style={{ width: 160 }}
        />
      </label>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.length === 0 && (
          <span className="muted" style={{ fontSize: 12 }}>
            Способы оплаты не добавлены.
          </span>
        )}
        {rows.map((r, i) => (
          <div
            key={r.id ?? i}
            data-testid={`flex-carrier-pay-${i}`}
            style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}
          >
            <select
              value={r.method}
              disabled={readOnly}
              onChange={(e) => setRow(i, { method: e.target.value as "cash" | "transfer" })}
              data-testid={`flex-carrier-pay-${i}-method`}
            >
              <option value="cash">{CARRIER_PAY_METHOD_LABELS.cash}</option>
              <option value="transfer">{CARRIER_PAY_METHOD_LABELS.transfer}</option>
            </select>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              value={r.amountStr}
              disabled={readOnly}
              onChange={(e) => setRow(i, { amountStr: e.target.value })}
              placeholder="₾"
              data-testid={`flex-carrier-pay-${i}-amount`}
              style={{ width: 100 }}
            />
            <input
              type="text"
              value={r.comment}
              disabled={readOnly}
              onChange={(e) => setRow(i, { comment: e.target.value })}
              placeholder="комментарий"
              data-testid={`flex-carrier-pay-${i}-comment`}
              style={{ flex: 1, minWidth: 120 }}
            />
            {!readOnly && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => removeRow(i)}
                data-testid={`flex-carrier-pay-${i}-remove`}
                aria-label="Удалить способ оплаты"
              >
                ✕
              </button>
            )}
          </div>
        ))}
        {!readOnly && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={addRow}
            data-testid="flex-carrier-pay-add"
            style={{ alignSelf: "flex-start" }}
          >
            + Добавить метод оплаты
          </button>
        )}
      </div>

      <div
        data-testid="flex-carrier-pay-balance"
        style={{
          fontSize: 12,
          color:
            balance > 0
              ? "var(--mnp-red, #dc2626)"
              : balance < 0
                ? "var(--mnp-amber, #d97706)"
                : fee > 0
                  ? "var(--mnp-green, #15803d)"
                  : undefined,
        }}
      >
        Оплачено {round2(paid)} из {round2(fee)} ₾ · {balanceLabel}
      </div>

      {!readOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={save}
            disabled={carrierPay.isPending}
            data-testid="flex-carrier-pay-save"
          >
            {carrierPay.isPending ? "…" : "Сохранить оплату"}
          </button>
          {saved && !carrierPay.isPending && (
            <span className="muted" style={{ fontSize: 12, color: "var(--mnp-green, #15803d)" }}>
              Сохранено
            </span>
          )}
        </div>
      )}

      {errMsg && (
        <p style={{ margin: 0, fontSize: 12, color: "var(--mnp-red, #dc2626)" }}>{errMsg}</p>
      )}
    </div>
  );
}

// Read-only places list, base places then «довоз» ones (FlexPlace.additional).
function PlacesView({ places }: { places: FlexPlace[] }) {
  if (places.length === 0) return <span className="muted">Не заполнено</span>;
  const base = places.filter((p) => !p.additional);
  const addl = places.filter((p) => p.additional);
  return (
    <div style={{ fontSize: 13, lineHeight: 1.7 }}>
      {base.map((p, i) => (
        <div key={`b${i}`} data-testid={`flex-place-view-${i}`}>
          • <span className="bold">Место {i + 1}</span> — {fmtPlace(p)}
        </div>
      ))}
      {addl.length > 0 && (
        <>
          <div className="bold" style={{ marginTop: 6 }}>
            Довоз:
          </div>
          {addl.map((p, i) => (
            <div key={`a${i}`} data-testid={`flex-addl-view-${i}`}>
              • <span className="bold">Довоз {i + 1}</span> — {fmtPlace(p)}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

// Editable field: in edit mode renders the right control for the field's kind
// (input / number / checkbox / select), bound to form[field]; otherwise renders
// the read-only `display` node. `field` типизирован ключами FIELD_KINDS —
// опечатка в имени поля в JSX не компилируется.
function EF({
  editing,
  form,
  setForm,
  field,
  display,
}: {
  editing: boolean;
  form: EditFormState;
  setForm: React.Dispatch<React.SetStateAction<EditFormState>>;
  field: FlexEditableField;
  display: ReactNode;
}): ReactNode {
  if (!editing) return display;
  const kind = FIELD_KINDS[field];
  const set = (val: string) => setForm((f) => ({ ...f, [field]: val }));
  const tid = `flex-edit-${field}`;
  if (kind === "bool") {
    return (
      <input
        type="checkbox"
        checked={form[field] === "true"}
        onChange={(e) => set(e.target.checked ? "true" : "")}
        data-testid={tid}
      />
    );
  }
  if (kind === "trielev") {
    return (
      <select value={form[field] ?? ""} onChange={(e) => set(e.target.value)} data-testid={tid}>
        <option value="">—</option>
        <option value="true">Есть</option>
        <option value="false">Нет</option>
      </select>
    );
  }
  if (kind === "customs") {
    return (
      <select value={form[field] ?? ""} onChange={(e) => set(e.target.value)} data-testid={tid}>
        <option value="">—</option>
        <option value="green">green</option>
        <option value="yellow">yellow</option>
        <option value="red">red</option>
      </select>
    );
  }
  const numeric = kind === "num" || kind === "intpos" || kind === "int" || kind === "intnn";
  return (
    <input
      type={numeric ? "number" : "text"}
      step={kind === "num" ? "0.01" : numeric ? "1" : undefined}
      value={form[field] ?? ""}
      onChange={(e) => set(e.target.value)}
      data-testid={tid}
      style={{ width: "100%" }}
    />
  );
}

function PhotoGrid({
  orderId,
  attachments,
}: {
  orderId: string;
  attachments: FlexOrderAttachment[];
}) {
  const photos = attachments.filter((a) => a.type === "photo");
  if (photos.length === 0) {
    return <div className="muted" style={{ fontSize: 13 }}>Фото не приложены</div>;
  }
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
        gap: 8,
      }}
    >
      {photos.map((p) => {
        const url = apiUrl(`/admin/flex-orders/${orderId}/attachments/${p.id}/file`);
        return (
          <a
            key={p.id}
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "block",
              aspectRatio: "1 / 1",
              overflow: "hidden",
              borderRadius: 6,
              background: "var(--mnp-bg-2, #f3f4f6)",
            }}
            title={p.fileName ?? p.id}
          >
            <img
              src={url}
              alt={p.fileName ?? "photo"}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              loading="lazy"
            />
          </a>
        );
      })}
    </div>
  );
}

/**
 * Drawer body for a Flex order. Renders the full user-submitted form (route,
 * sender, recipient, pickup, parcel parameters, content description, photo
 * attachments) plus admin processing fields and status timeline.
 */
export function FlexBody({ id, onClose }: { id: string; onClose: () => void }) {
  // Мутационные кнопки гейтятся ФАКТИЧЕСКИМ правом orders:status из единой
  // shared-таблицы — ровно тем, которым гейтятся все мутационные роуты
  // flex/shop на API (ревью rbac-single-source: гейт по module-access тихо
  // отбирал рабочие кнопки у fulfillment/consoles, у которых право ЕСТЬ).
  // UI совпадает с API для всех ролей автоматически; chats (без права) видит
  // read-only. Дефолт вне RoleProvider — 'chats': консервативно, fail-closed.
  const role = useOptionalRole();
  const canMutate = hasPermission(role, "orders:status");
  const flexQ = useFlexOrder(id);
  const { data, isLoading } = flexQ;
  const { data: attachmentsRaw } = useFlexOrderAttachments(id);
  // Be defensive: in unit tests a single api-mock can return non-array shapes
  // for this endpoint (it doesn't know to special-case the attachments URL).
  // In production the route always emits an array, so this is a no-op there.
  const attachments: FlexOrderAttachment[] = Array.isArray(attachmentsRaw)
    ? attachmentsRaw
    : [];
  const transition = useFlexStatusTransition(id);
  const update = useFlexOrderUpdate(id);
  // Review-цикл (2026-05-29): admin actions из in_review — Принять/Отклонить.
  const approve = useFlexApprove(id);
  const reject = useFlexReject(id);
  // Reject-modal state: открыта/нет + текст причины + локальная ошибка
  // валидации (отдельно от сетевой ошибки, которая на reject.error).
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditFormState>(() => emptyForm(data));
  // Снапшот исходных значений формы на момент входа в режим правки (и при
  // каждом re-seed вне правки). formToPatch сравнивает форму с ним и шлёт
  // ТОЛЬКО изменённые поля — нетронутая форма даёт пустой PATCH, поле, не
  // пришедшее с сервера, больше не затирается null-ом (шаг 4 карточки
  // flex-wire-contract-shared). Если data обновилась ПОКА админ правит
  // (например, после «Сохранить места и посчитать»), снапшот сознательно НЕ
  // обновляется: иначе незатронутое поле сравнилось бы со свежим серверным
  // значением и уехало бы в PATCH со стейл-значением формы.
  const [formBaseline, setFormBaseline] = useState<EditFormState>(() => emptyForm(data));
  // «Итого» kind поднят сюда: при выборе «Итоговая» под панелью разворачивается
  // выделенный блок «Места» (Task A). recomputeFinalKey бампается из этого блока
  // по кнопке «Сохранить места и посчитать», чтобы панель пересчитала €/₾.
  const [totalKind, setTotalKind] = useState<FlexAnnouncedKind>("preliminary");
  const [recomputeFinalKey, setRecomputeFinalKey] = useState(0);
  // Изменили места после объявления итога → снова разрешаем «Сохранить и
  // посчитать». Сбрасывается при перезагрузке записи и после успешного сохранения.
  const [placesDirty, setPlacesDirty] = useState(false);

  // Sender passport reveal / edit. `passportRevealed` holds the decrypted
  // plaintext after the audited GET. `passportEdited` is non-null only while
  // the admin is typing a new value in edit mode — formToPatch deliberately
  // does NOT see senderPassport, so we wire it into the patch ourselves on
  // save (and we never include it when the admin hasn't actually changed it,
  // so accidental open-of-edit doesn't trigger a passport write).
  const [passportRevealed, setPassportRevealed] = useState<string | null>(null);
  const [passportEdited, setPassportEdited] = useState<string | null>(null);
  const [passportLoading, setPassportLoading] = useState(false);
  const [passportError, setPassportError] = useState(false);
  useEffect(() => {
    setPassportRevealed(null);
    setPassportEdited(null);
    setPassportLoading(false);
    setPassportError(false);
  }, [id]);
  async function revealPassport() {
    setPassportLoading(true);
    setPassportError(false);
    try {
      const r = await api<{ senderPassport: string | null }>(
        `/admin/flex-orders/${id}/sender-passport`,
      );
      setPassportRevealed(r.senderPassport ?? "");
    } catch {
      setPassportError(true);
    } finally {
      setPassportLoading(false);
    }
  }
  const [baseRows, setBaseRows] = useState<PlaceRow[]>(() => seedPlaces(data).base);
  const [addlRows, setAddlRows] = useState<PlaceRow[]>(() => seedPlaces(data).addl);
  const [additionalDelivery, setAdditionalDelivery] = useState<boolean>(
    () => !!(data as { additionalDelivery?: boolean } | undefined)?.additionalDelivery,
  );
  // Re-seed the form whenever the underlying record changes (initial load,
  // re-fetch after status change, etc.) so we don't carry over stale edits.
  function reseedPlaces() {
    const s = seedPlaces(data);
    setBaseRows(s.base);
    setAddlRows(s.addl);
    setAdditionalDelivery(
      !!(data as { additionalDelivery?: boolean } | undefined)?.additionalDelivery,
    );
    setPlacesDirty(false);
  }
  // Re-seed формы + снапшота-baseline из текущей записи (единой парой — иначе
  // дельта сравнивала бы форму с устаревшим снапшотом).
  function reseedForm() {
    const seeded = emptyForm(data);
    setForm(seeded);
    setFormBaseline(seeded);
  }
  useEffect(() => {
    if (!editing) {
      reseedForm();
      // Несохранённые правки мест (вне режима «Править») не стираем фоновым
      // рефетчем (window-focus, blur-сохранение веса, второй админ) — ревью
      // flex-wire-contract: дельта их честно не шлёт, потеря была бы бесследной.
      if (!placesDirty) reseedPlaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, editing]);

  function startEdit() {
    reseedForm();
    reseedPlaces();
    setEditing(true);
  }
  function cancelEdit() {
    setEditing(false);
    reseedForm();
    setPassportEdited(null);
    reseedPlaces();
  }
  function saveEdit() {
    const places: FlexPlace[] = [
      ...baseRows.map((r) => rowToPlace(r, false)),
      ...addlRows.map((r) => rowToPlace(r, true)),
    ];
    update.mutate(
      {
        // Дельта против снапшота: только изменённые поля (см. formToPatch).
        ...formToPatch(form, formBaseline),
        // Only include senderPassport if the admin actually edited it (revealed
        // + typed). Otherwise we'd wipe the existing encrypted value on every
        // save where the user didn't touch the passport at all.
        ...(passportEdited !== null ? { senderPassport: passportEdited } : {}),
        // Места — тоже дельтой: блок уходит только если админ реально трогал
        // редактор мест (placesDirty). Иначе нетронутая форма пересылала бы
        // round-trip placesJson (с побочным oversized|=crateNeeded) и
        // материализовала бы пустые места из pieces при сохранении текстового
        // поля.
        ...(placesDirty
          ? {
              placesJson: places,
              additionalDelivery,
              additionalPlacesCount: addlRows.length,
            }
          : {}),
      },
      {
        onSuccess: () => {
          setEditing(false);
          setPassportEdited(null);
        },
      },
    );
  }
  // «Сохранить места и посчитать» (блок под «Итого», Task A): персистит места,
  // НЕ выходя из/входя в режим «Править», и бампает recomputeFinalKey, чтобы
  // панель пересчитала итоговую сумму по свежему весу из мест (Task B).
  function savePlacesAndRecompute() {
    const places: FlexPlace[] = [
      ...baseRows.map((r) => rowToPlace(r, false)),
      ...addlRows.map((r) => rowToPlace(r, true)),
    ];
    update.mutate(
      { placesJson: places, additionalDelivery, additionalPlacesCount: addlRows.length },
      {
        onSuccess: () => {
          setRecomputeFinalKey((k) => k + 1);
          setPlacesDirty(false);
        },
      },
    );
  }
  // Любая правка мест помечает форму «грязной» — это снова включает кнопку
  // «Сохранить места и посчитать» после того, как итог уже был объявлен.
  const setBase = (i: number, patch: Partial<PlaceRow>) => {
    setPlacesDirty(true);
    setBaseRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const addBase = () => {
    setPlacesDirty(true);
    setBaseRows((rows) => [...rows, emptyPlaceRow()]);
  };
  const removeBase = (i: number) => {
    setPlacesDirty(true);
    setBaseRows((rows) => rows.filter((_, idx) => idx !== i));
  };
  const setAddl = (i: number, patch: Partial<PlaceRow>) => {
    setPlacesDirty(true);
    setAddlRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeAddl = (i: number) => {
    setPlacesDirty(true);
    setAddlRows((rows) => rows.filter((_, idx) => idx !== i));
  };
  // «Довоз» toggle: off clears extra places; on opens one row to fill.
  const toggleAdditional = (on: boolean) => {
    setPlacesDirty(true);
    setAdditionalDelivery(on);
    if (!on) setAddlRows([]);
    else setAddlRows((rows) => (rows.length === 0 ? [emptyPlaceRow()] : rows));
  };
  const setAddlCount = (n: number) => {
    setPlacesDirty(true);
    const target = Math.max(0, Math.min(50, Math.floor(Number.isFinite(n) ? n : 0)));
    setAddlRows((rows) =>
      target <= rows.length
        ? rows.slice(0, target)
        : [...rows, ...Array.from({ length: target - rows.length }, emptyPlaceRow)],
    );
  };

  // Inline editable-field helper bound to this body's edit state.
  const ef = (field: FlexEditableField, display: ReactNode): ReactNode => (
    <EF editing={editing} form={form} setForm={setForm} field={field} display={display} />
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const status = data?.status as FlexStatus | undefined;
  // Прод-граница = «Прибыло на склад» (pickup). Forward в пост-pickup
  // logistics-статусы скрыт, пока логистика выключена; дальше заказ ведёт
  // процесс прод-воронки AMO. Контракт «статус ↔ AMO» (2026-05-30).
  const flexLogisticsEnabled =
    import.meta.env.VITE_FLEX_LOGISTICS_ENABLED === "true";
  const rawNext = status ? neighborFlexStatus(status, 1) : null;
  const next =
    rawNext && (flexLogisticsEnabled || !FLEX_LOGISTICS_STATUSES.includes(rawNext))
      ? rawNext
      : null;
  const prev = status ? neighborFlexStatus(status, -1) : null;
  const currentIdx = status ? FLEX_STATUSES.indexOf(status) : -1;
  // Review-фазы (2026-05-29): submitted/in_review/rejected — пока админ не
  // «Принял в работу», скрываем панель «Итого» и поднимаем фото+описание
  // в самый верх drawer body. Кнопки prev/next/Edit заменяются на
  // Принять/Отклонить в фазе in_review.
  const isPreReview = !!status && FLEX_PRE_REVIEW_STATUSES.has(status);
  const isInReview = status === "in_review";
  const isRejected = status === "rejected";
  // Тип отправки (Личные вещи/Коммерция) показываем только для маршрутов в/из
  // РФ/Казахстана/Беларуси (Task C). Актуальный коридор Грузия→Европа — скрыт.
  const showShipmentType = flexSupportsShipmentType(
    data?.originCountry,
    data?.destinationCountry,
  );
  // Блок «Места» под «Итого» — когда выбрана «Итоговая» и не идёт полное
  // редактирование и не review-фаза (Task A). Read-only роли блок не нужен:
  // он существует ради «Сохранить места и посчитать» (мутация).
  const showFinalPlaces =
    totalKind === "final" && !editing && !isPreReview && canMutate;
  // Печать наклеек по местам — доступна начиная с «Принят в работу» (approved)
  // и на любом последующем этапе. Не завязана на статус-флоу: после pickup
  // логистику ведёт воронка AMO, но наклейки печатать всё равно нужно.
  const canPrintLabels = !!status && !isPreReview && status !== "draft";
  // Итоговая сумма уже объявлена — блок «Сохранить места и посчитать»
  // показываем «нажатым», пока места не изменили (см. placesDirty).
  const finalAnnounced =
    data?.announcedKind === "final" && data?.announcedAmountGel != null;

  function submitReject() {
    setRejectError(null);
    const trimmed = rejectReason.trim();
    if (trimmed.length < 5) {
      setRejectError("Причина должна быть не короче 5 символов.");
      return;
    }
    reject.mutate(trimmed, {
      onSuccess: () => {
        setRejectOpen(false);
        setRejectReason("");
      },
      onError: (err) => {
        const e = err as { body?: { message?: string }; message?: string };
        setRejectError(e?.body?.message ?? e?.message ?? "Не удалось отклонить");
      },
    });
  }

  const senderAddrStruct = data?.senderAddressStructured ?? null;
  const senderAddr =
    data?.senderAddressLine ||
    joinAddr([
      senderAddrStruct?.street,
      senderAddrStruct?.building,
      senderAddrStruct?.apartment,
      senderAddrStruct?.city,
      senderAddrStruct?.zip,
    ]);

  const recipientAddr = joinAddr([
    data?.recipientStreet,
    data?.recipientBuilding,
    data?.recipientApartment,
    data?.recipientCity,
    data?.recipientZip,
  ]);

  // The user-side POST persists the description twice: once as a `note`
  // attachment row and once denormalized into `flexContentDescription`.
  // Skip notes whose description already shows up in «Содержимое» so we
  // don't render the same text twice in two adjacent sections.
  const contentText =
    (data?.flexContentDescription || data?.contents || "").trim();
  const notes = attachments.filter(
    (a) =>
      a.type === "note" &&
      a.description &&
      a.description.trim() !== contentText,
  );

  const title = data?.requestNumber ?? (data ? shortId(data.id) : "");

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <button className="btn-admin ghost" aria-label="Закрыть заказ" onClick={onClose}>
            <Icon name="close" />
          </button>
          {!data ? (
            <h2>{isLoading ? "Загрузка…" : "Flex-заказ"}</h2>
          ) : (
            <>
              <h2>{title}</h2>
              <span className={"badge-a " + FLEX_STATUS_BADGE[data.status]}>
                Flex · {FLEX_STATUS_LABELS_RU[data.status]}
              </span>
              {data.amoStatus === "not_configured" && (
                // §4.13: воронка/этапы AMO не заданы в настройках кассы —
                // синк по этой заявке пропущен, сделка в AMO не создавалась/
                // не обновлялась.
                <span
                  className="badge-a red"
                  title="Воронка и этапы AMO не заданы в настройках кассы — синк Flex→AMO выключен"
                  data-testid="flex-amo-not-configured"
                >
                  {FLEX_AMO_SYNC_STATUS_LABELS_RU.not_configured}
                </span>
              )}
              {data.amoStatus === "blocked_env" && (
                // §7.3a: воронка из настроек не входит в env-allowlist
                // AMO_ALLOWED_PIPELINE_IDS этого окружения — синк по заявке
                // заблокирован (тестовый стенд не пишет в прод-воронку).
                <span
                  className="badge-a red"
                  title="Воронка AMO не входит в AMO_ALLOWED_PIPELINE_IDS этого окружения — синк Flex→AMO заблокирован"
                  data-testid="flex-amo-blocked-env"
                >
                  {FLEX_AMO_SYNC_STATUS_LABELS_RU.blocked_env}
                </span>
              )}
              <div className="grow" />
              {!canMutate ? (
                // Read-only роль: ни одной мутационной кнопки (они дали бы 403).
                <span
                  style={{ color: "var(--mnp-fg-3)", fontSize: 12 }}
                  data-testid="flex-readonly"
                >
                  Режим просмотра
                </span>
              ) : editing ? (
                <>
                  <button
                    className="btn-admin ghost"
                    onClick={cancelEdit}
                    disabled={update.isPending}
                  >
                    Отменить
                  </button>
                  <button
                    className="btn-admin primary"
                    onClick={saveEdit}
                    disabled={update.isPending}
                    data-testid="flex-edit-save"
                  >
                    {update.isPending ? "Сохранение…" : "Сохранить"}
                  </button>
                </>
              ) : isInReview ? (
                <>
                  <button
                    className="btn-admin ghost"
                    onClick={() => {
                      setRejectError(null);
                      setRejectReason("");
                      setRejectOpen(true);
                    }}
                    disabled={reject.isPending || approve.isPending}
                    data-testid="flex-reject-open"
                  >
                    Отклонить
                  </button>
                  <button
                    className="btn-admin primary"
                    onClick={() => approve.mutate()}
                    disabled={approve.isPending || reject.isPending}
                    data-testid="flex-approve"
                  >
                    {approve.isPending ? "…" : "Принять в работу"}
                  </button>
                </>
              ) : status === "submitted" || isRejected ? (
                // submitted — auto-flip в in_review при GET уже произошёл.
                // rejected — ждём, когда юзер пришлёт правки (Отправить
                // повторно), кнопок действия у админа здесь нет.
                <span
                  style={{ color: "var(--mnp-fg-3)", fontSize: 12 }}
                  data-testid="flex-no-actions"
                >
                  {isRejected ? "Ждём повторной отправки" : ""}
                </span>
              ) : (
                <>
                  {prev && (
                    <button
                      className="btn-admin ghost"
                      onClick={() => transition.mutate(prev)}
                      disabled={transition.isPending}
                      data-testid="flex-status-prev"
                      title={`← ${FLEX_STATUS_LABELS_RU[prev]}`}
                    >
                      ← {FLEX_STATUS_LABELS_RU[prev]}
                    </button>
                  )}
                  <button
                    className="btn-admin"
                    onClick={startEdit}
                    data-testid="flex-edit-start"
                  >
                    Править
                  </button>
                  {next && (
                    <button
                      className="btn-admin primary"
                      onClick={() => transition.mutate(next)}
                      disabled={transition.isPending}
                      data-testid="flex-status-next"
                    >
                      → {FLEX_STATUS_LABELS_RU[next]}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {data && (
          <div className="drawer-body">
            {/* Review-фаза (2026-05-29): фото + описание в самый верх, чтобы
                админ сразу видел, что прислал юзер, и решал — принять или
                отклонить. «Итого» в этой фазе скрыт. */}
            {isPreReview && (
              <div
                data-testid="flex-pre-review-block"
                style={{
                  background: "var(--mnp-bg-alt, #f8fafc)",
                  border: "1px solid var(--mnp-border-soft, #e2e8f0)",
                  borderRadius: 12,
                  padding: "14px 16px",
                  marginBottom: 14,
                }}
              >
                <p className="eyebrow-a" style={{ marginTop: 0 }}>
                  Что прислал клиент
                </p>
                {contentText ? (
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: 13,
                      lineHeight: 1.5,
                      margin: "6px 0 12px",
                    }}
                  >
                    {contentText}
                  </p>
                ) : (
                  <p
                    style={{ color: "var(--mnp-fg-3)", fontSize: 12, margin: "6px 0 12px" }}
                  >
                    Описание не заполнено.
                  </p>
                )}
                <PhotoGrid orderId={data.id} attachments={attachments} />
              </div>
            )}
            {/* Reject-баннер с последней причиной (rejected_reason пишется при
                каждом reject; вся история — в OrderStatusLog с reason). */}
            {isRejected && data.rejectedReason && (
              <div
                data-testid="flex-rejected-banner"
                style={{
                  background: "#FDECED",
                  border: "1px solid #F5B5B7",
                  borderLeft: "3px solid var(--mnp-red, #c00)",
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 14,
                  fontSize: 12,
                  lineHeight: 1.4,
                  color: "var(--mnp-red-dark, #a40000)",
                }}
              >
                <b style={{ display: "block", marginBottom: 4 }}>Отклонено</b>
                <span style={{ whiteSpace: "pre-wrap" }}>{data.rejectedReason}</span>
                {data.rejectedAt && (
                  <div style={{ marginTop: 6, color: "var(--mnp-fg-3)", fontSize: 11 }}>
                    {formatDateTime(data.rejectedAt)}
                  </div>
                )}
              </div>
            )}
            {/* Забор · перевозчик: только для курьерского забора (не самопривоз)
                и после принятия в работу (не в review-фазе). */}
            {!isPreReview && data.selfDeliver === false && (
              <FlexCarrierPanel
                orderId={data.id}
                pickupCarrierRef={data.pickupCarrierRef}
                pickupDate={data.pickupDate}
                pickupDay={data.pickupDay}
                pickupSlot={data.pickupSlot}
                readOnly={!canMutate}
              />
            )}
            {!isPreReview && (
              <FlexTotalPanel
                orderId={data.id}
                weightKg={data.weightKg}
                updatedAt={data.updatedAt}
                tariffCategory={data.tariffCategory}
                announcedAmountEur={data.announcedAmountEur}
                announcedAmountGel={data.announcedAmountGel}
                announcedKind={data.announcedKind}
                announcedAt={data.announcedAt}
                paidAt={data.paidAt}
                paidAmountGel={data.paidAmountGel}
                onPaid={() => flexQ.refetch?.()}
                showShipmentType={showShipmentType}
                kind={totalKind}
                onKindChange={setTotalKind}
                recomputeFinalKey={recomputeFinalKey}
                readOnly={!canMutate}
              />
            )}
            {/* Забор · оплата перевозчику: рядом с «Итого», только для
                курьерского забора (не самопривоз) после принятия в работу. */}
            {!isPreReview && data.selfDeliver === false && (
              <FlexCarrierPaymentPanel
                orderId={data.id}
                carrierFeeGel={data.carrierFeeGel}
                carrierPayments={data.carrierPaymentsJson}
                readOnly={!canMutate}
              />
            )}
            {/* Task A: при выборе «Итоговая» поля мест выезжают прямо под
                карточкой «Итого» и выделяются — их и надо заполнить, чтобы
                посчитать итог по объёмному/фактическому весу. */}
            {showFinalPlaces && (
              <div
                data-testid="flex-final-places"
                style={{
                  border: "2px solid var(--mnp-green, #15803d)",
                  background: "var(--mnp-green-50, #f0fdf4)",
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span className="bold" style={{ fontSize: 14 }}>
                    Места — заполните для итоговой суммы
                  </span>
                  <span className="muted" style={{ fontSize: 11 }}>
                    вес + габариты по каждому месту; для негабарита берётся объёмный вес
                  </span>
                </div>
                <PlacesEditor
                  baseRows={baseRows}
                  addlRows={addlRows}
                  additionalDelivery={additionalDelivery}
                  setBase={setBase}
                  addBase={addBase}
                  removeBase={removeBase}
                  setAddl={setAddl}
                  removeAddl={removeAddl}
                  toggleAdditional={toggleAdditional}
                  setAddlCount={setAddlCount}
                />
                <button
                  type="button"
                  className="btn-admin primary"
                  onClick={savePlacesAndRecompute}
                  disabled={update.isPending || (finalAnnounced && !placesDirty)}
                  data-testid="flex-final-places-save"
                  style={{ alignSelf: "flex-start" }}
                >
                  {update.isPending
                    ? "Сохранение…"
                    : finalAnnounced && !placesDirty
                      ? "Места сохранены ✓"
                      : "Сохранить места и посчитать"}
                </button>
              </div>
            )}
            {/* Печать наклеек по местам — с «Принят в работу» и далее. */}
            {canPrintLabels && (
              <div
                data-testid="flex-preparing-block"
                style={{
                  border: "1px solid var(--mnp-border, #e5e7eb)",
                  background: "var(--mnp-bg-alt, #f8fafc)",
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 12,
                }}
              >
                <p className="eyebrow-a" style={{ marginTop: 0 }}>
                  Наклейки по местам
                </p>
                <PlacesView places={Array.isArray(data.placesJson) ? data.placesJson : []} />
                <button
                  type="button"
                  className="btn-admin"
                  data-testid="flex-print-labels"
                  title="Открыть PDF наклеек (A6, по 3 на лист)"
                  style={{ marginTop: 8 }}
                  onClick={() =>
                    window.open(
                      apiUrl(`/admin/flex-orders/${data.id}/labels.pdf`),
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  Печать наклеек по местам
                </button>
                <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>
                  Трек и QR — из сделки AMO
                </span>
              </div>
            )}
            {editing && (
              <div
                className="dr-edit-banner"
                data-testid="flex-edit-banner"
                role="status"
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  padding: "10px 14px",
                  background: "var(--mnp-teal-50, #ecfdf5)",
                  border: "1px solid var(--mnp-teal-200, #99f6e4)",
                  borderRadius: 8,
                  marginBottom: 12,
                  fontSize: 12,
                  lineHeight: 1.4,
                }}
              >
                <Icon name="edit" size={14} />
                <div>
                  <b>Режим редактирования</b>
                  <span style={{ color: "var(--mnp-fg-2)" }}>
                    {" · admin-поля стали инпутами (вес, размеры, customs-риск, адреса). User-поля заявки read-only."}
                  </span>
                </div>
              </div>
            )}
            <p className="eyebrow-a">Статус</p>
            <div className="status-row">
              {FLEX_STATUSES.map((s) => {
                const myIdx = FLEX_STATUSES.indexOf(s);
                return (
                  <div
                    key={s}
                    className="s"
                    data-done={myIdx < currentIdx ? "true" : undefined}
                    data-current={myIdx === currentIdx ? "true" : undefined}
                  >
                    {FLEX_STATUS_LABELS_RU[s]}
                  </div>
                );
              })}
            </div>
            {transition.isError && (
              <p
                data-testid="flex-status-error"
                style={{ color: "var(--mnp-red, #c00)", fontSize: 12, marginTop: 4 }}
              >
                {(() => {
                  const err = transition.error as { body?: { message?: string }; message?: string };
                  return err?.body?.message ?? err?.message ?? "Не удалось сменить статус";
                })()}
              </p>
            )}

            <p className="eyebrow-a">Маршрут</p>
            <dl className="inline-grid">
              <Row label="Откуда" value={ef("originCountry", countryLabel(data.originCountry))} />
              <Row label="Куда" value={ef("destinationCountry", countryLabel(data.destinationCountry))} />
            </dl>

            <p className="eyebrow-a">Клиент</p>
            <dl className="inline-grid">
              <Row label="Telegram chatId" value={data.chatId} />
              <Row
                label="Telegram"
                value={
                  <a
                    className="tg-deeplink"
                    href={telegramHref(data.user?.tgUsername ?? data.user?.username, data.chatId)}
                    target="_blank"
                    rel="noreferrer"
                    title="Открыть Telegram клиента"
                  >
                    <span className="tg-deeplink-ico">TG</span>
                    {data.user?.username ? `@${data.user.username}` : "Открыть профиль ↗"}
                  </a>
                }
              />
              <Row label="Предзаказ" value={ef("preorder", data.preorder ? "Да" : "Нет")} />
            </dl>

            <p className="eyebrow-a">Отправитель</p>
            <dl className="inline-grid">
              <Row label="ФИО" value={ef("senderFullName", data.senderFullName ?? "—")} />
              <Row
                label="Телефон"
                value={
                  editing ? (
                    <span style={{ display: "inline-flex", gap: 6, width: "100%" }}>
                      {ef("senderPhoneCode", "")}
                      {ef("senderPhoneLocal", "")}
                    </span>
                  ) : (
                    fullPhone(data.senderPhoneCode, data.senderPhoneLocal)
                  )
                }
              />
              <Row
                label="Паспорт"
                value={
                  editing ? (
                    passportRevealed !== null ? (
                      <input
                        data-testid="flex-edit-senderPassport"
                        type="text"
                        value={passportEdited ?? passportRevealed}
                        onChange={(e) => setPassportEdited(e.target.value)}
                        placeholder="серия и номер"
                      />
                    ) : (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <span className="muted">
                          {passportError
                            ? "ошибка загрузки"
                            : (data.senderPassportMasked ?? "—")}
                        </span>
                        <button
                          type="button"
                          className="btn-admin ghost"
                          style={{ fontSize: 12, padding: "2px 8px" }}
                          onClick={revealPassport}
                          disabled={passportLoading}
                        >
                          {passportLoading ? "…" : "Показать для редактирования"}
                        </button>
                      </span>
                    )
                  ) : (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span>
                        {passportRevealed !== null
                          ? passportRevealed || "—"
                          : passportError
                            ? "ошибка загрузки"
                            : (data.senderPassportMasked ?? "—")}
                      </span>
                      {data.senderPassportMasked &&
                        (passportRevealed !== null ? (
                          <button
                            type="button"
                            className="btn-admin ghost"
                            style={{ fontSize: 12, padding: "2px 8px" }}
                            onClick={() => setPassportRevealed(null)}
                          >
                            Скрыть
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-admin ghost"
                            style={{ fontSize: 12, padding: "2px 8px" }}
                            onClick={revealPassport}
                            disabled={passportLoading}
                          >
                            {passportLoading ? "…" : "Показать"}
                          </button>
                        ))}
                    </span>
                  )
                }
              />
              <Row label="Адрес" value={ef("senderAddressLine", senderAddr)} />
            </dl>

            <p className="eyebrow-a">Получатель</p>
            <dl className="inline-grid">
              <Row
                label="ФИО"
                value={ef(
                  "recipientFullName",
                  data.recipientFullName ?? <span className="muted">— (не указано)</span>,
                )}
              />
              <Row label="Страна" value={ef("recipientCountry", countryLabel(data.recipientCountry))} />
              <Row
                label="Город"
                value={ef("recipientCity", data.recipientCity ?? <span className="muted">—</span>)}
              />
              {editing && <Row label="Нет телефона" value={ef("recipientMissingPhone", "")} />}
              <Row
                label="Телефон"
                value={
                  editing ? (
                    <span style={{ display: "inline-flex", gap: 6, width: "100%" }}>
                      {ef("recipientPhoneCode", "")}
                      {ef("recipientPhoneLocal", "")}
                    </span>
                  ) : data.recipientMissingPhone ? (
                    <span className="muted">— (не указано клиентом)</span>
                  ) : (
                    fullPhone(data.recipientPhoneCode, data.recipientPhoneLocal)
                  )
                }
              />
              <Row label="Мессенджер" value={ef("recipientMessenger", data.recipientMessenger ?? "—")} />
              {editing ? (
                <>
                  <Row label="Нет адреса" value={ef("recipientMissingAddress", "")} />
                  <Row label="Улица" value={ef("recipientStreet", "")} />
                  <Row label="Дом" value={ef("recipientBuilding", "")} />
                  <Row label="Квартира" value={ef("recipientApartment", "")} />
                  <Row label="Индекс" value={ef("recipientZip", "")} />
                </>
              ) : (
                <Row
                  label="Адрес"
                  value={
                    data.recipientMissingAddress ? (
                      <span className="muted">— (не указано клиентом)</span>
                    ) : (
                      recipientAddr
                    )
                  }
                />
              )}
              <Row label="Email" value={ef("recipientEmail", data.recipientEmail ?? "—")} />
              <Row
                label="Гео"
                value={ef(
                  "recipientGeoUrl",
                  data.recipientGeoUrl ? (
                    <a href={data.recipientGeoUrl} target="_blank" rel="noreferrer">
                      Открыть карту ↗
                    </a>
                  ) : (
                    "—"
                  ),
                )}
              />
            </dl>

            {(editing || data.selfDeliver === false) && (
              <>
                <p className="eyebrow-a">Забор</p>
                <dl className="inline-grid">
                  {editing && (
                    <>
                      <Row label="Самопривоз (без курьера)" value={ef("selfDeliver", "")} />
                      <Row label="Забор = адрес отправителя" value={ef("pickupSameAsSender", "")} />
                    </>
                  )}
                  <Row label="Город" value={ef("pickupCity", data.pickupCity ?? "—")} />
                  <Row label="Адрес (admin)" value={ef("pickupAddress", data.pickupAddress ?? "—")} />
                  <Row
                    label="Гео"
                    value={ef(
                      "pickupGeoUrl",
                      data.pickupGeoUrl ? (
                        <a href={data.pickupGeoUrl} target="_blank" rel="noreferrer">
                          Открыть карту ↗
                        </a>
                      ) : (
                        "—"
                      ),
                    )}
                  />
                  <Row label="Этаж" value={ef("pickupFloor", data.pickupFloor ?? "—")} />
                  <Row
                    label="Лифт"
                    value={ef(
                      "pickupHasElevator",
                      data.pickupHasElevator === true
                        ? "Есть"
                        : data.pickupHasElevator === false
                        ? "Нет"
                        : "—",
                    )}
                  />
                  <Row label="Телефон" value={ef("pickupPhoneLocal", data.pickupPhoneLocal ?? "—")} />
                  <Row label="День" value={ef("pickupDay", data.pickupDay ?? "—")} />
                  <Row label="Слот" value={ef("pickupSlot", data.pickupSlot ?? "—")} />
                </dl>
              </>
            )}

            <p className="eyebrow-a">Параметры посылки</p>
            <dl className="inline-grid">
              <Row label="Мест" value={ef("pieces", data.pieces ?? "—")} />
              <Row
                label="Заявл. стоимость, €"
                value={ef("declaredValueEur", data.declaredValueEur ?? "—")}
              />
              <Row
                label="Предв. вес, кг"
                value={
                  data.weightKg != null ? (
                    <span>
                      {data.weightKg}{" "}
                      <span className="muted" style={{ fontSize: 11 }}>
                        (вводится в «Итого»)
                      </span>
                    </span>
                  ) : (
                    <span style={{ color: "var(--mnp-red, #c00)", fontWeight: 600 }}>
                      не введён — укажите в «Итого»
                    </span>
                  )
                }
              />
              <Row
                label="Размеры, см (Д×Ш×В)"
                value={
                  editing ? (
                    <span style={{ display: "inline-flex", gap: 4 }}>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={form.dimensionsLengthCm}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            dimensionsLengthCm: e.target.value,
                          }))
                        }
                        placeholder="Д"
                        data-testid="flex-edit-dim-l"
                        style={{ width: 60 }}
                      />
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={form.dimensionsWidthCm}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            dimensionsWidthCm: e.target.value,
                          }))
                        }
                        placeholder="Ш"
                        data-testid="flex-edit-dim-w"
                        style={{ width: 60 }}
                      />
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={form.dimensionsHeightCm}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            dimensionsHeightCm: e.target.value,
                          }))
                        }
                        placeholder="В"
                        data-testid="flex-edit-dim-h"
                        style={{ width: 60 }}
                      />
                    </span>
                  ) : (
                    fmtDimensions(
                      data.dimensionsLengthCm,
                      data.dimensionsWidthCm,
                      data.dimensionsHeightCm,
                    )
                  )
                }
              />
              <Row
                label="Обрешётка"
                value={
                  editing ? (
                    <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                      {ef("crateNeeded", "")} нужна · кол-во {ef("crateCount", "")}
                    </span>
                  ) : data.crateNeeded ? (
                    `Нужна${data.crateCount ? ` · ${data.crateCount} шт` : ""}`
                  ) : (
                    "Не нужна"
                  )
                }
              />
              <Row
                label="Хранение"
                value={
                  editing ? (
                    <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                      {ef("storageNeeded", "")} нужно · до {ef("storageUntil", "")}
                    </span>
                  ) : data.storageNeeded ? (
                    `Нужно${data.storageUntil ? ` до ${data.storageUntil}` : ""}`
                  ) : (
                    "Не нужно"
                  )
                }
              />
              <Row
                label="Риск таможни"
                value={
                  editing ? (
                    <select
                      value={form.customsRiskLevel}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          customsRiskLevel: e.target
                            .value as EditFormState["customsRiskLevel"],
                        }))
                      }
                      data-testid="flex-edit-customs"
                    >
                      <option value="">—</option>
                      <option value="green">green</option>
                      <option value="yellow">yellow</option>
                      <option value="red">red</option>
                    </select>
                  ) : (
                    data.customsRiskLevel ?? "—"
                  )
                }
              />
              <Row
                label="Адрес забора (admin)"
                value={
                  editing ? (
                    <input
                      type="text"
                      value={form.pickupAddress}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, pickupAddress: e.target.value }))
                      }
                      placeholder="Внутренний адрес забора"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    data.pickupAddress ?? "—"
                  )
                }
              />
              <Row
                label="Адрес доставки (admin)"
                value={
                  editing ? (
                    <input
                      type="text"
                      value={form.deliveryAddress}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          deliveryAddress: e.target.value,
                        }))
                      }
                      placeholder="Внутренний адрес доставки"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    data.deliveryAddress ?? "—"
                  )
                }
              />
            </dl>

            <p className="eyebrow-a">Места (параметры по-местно)</p>
            {editing ? (
              <PlacesEditor
                baseRows={baseRows}
                addlRows={addlRows}
                additionalDelivery={additionalDelivery}
                setBase={setBase}
                addBase={addBase}
                removeBase={removeBase}
                setAddl={setAddl}
                removeAddl={removeAddl}
                toggleAdditional={toggleAdditional}
                setAddlCount={setAddlCount}
              />
            ) : (
              <PlacesView places={Array.isArray(data.placesJson) ? data.placesJson : []} />
            )}

            {update.isError && (
              <p style={{ color: "var(--mnp-red, #c00)", fontSize: 12 }}>
                Не удалось сохранить: {String((update.error as Error)?.message)}
              </p>
            )}

            <p className="eyebrow-a">Содержимое (от клиента)</p>
            {editing ? (
              <div className="flex-col" style={{ gap: 6 }}>
                <textarea
                  value={form.flexContentDescription ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, flexContentDescription: e.target.value }))
                  }
                  placeholder="Описание содержимого (от клиента)"
                  data-testid="flex-edit-flexContentDescription"
                  rows={3}
                  style={{ width: "100%" }}
                />
                <textarea
                  value={form.contents ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, contents: e.target.value }))}
                  placeholder="Содержимое (admin)"
                  data-testid="flex-edit-contents"
                  rows={2}
                  style={{ width: "100%" }}
                />
              </div>
            ) : (
              <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
                {data.flexContentDescription || data.contents || (
                  <span className="muted">Не указано</span>
                )}
              </div>
            )}

            <p className="eyebrow-a">Фото от клиента</p>
            <PhotoGrid orderId={data.id} attachments={attachments} />

            {notes.length > 0 && (
              <>
                <p className="eyebrow-a">Заметки от клиента</p>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                  {notes.map((n) => (
                    <div key={n.id} style={{ marginBottom: 6 }}>
                      • {n.description}
                    </div>
                  ))}
                </div>
              </>
            )}

            <p className="eyebrow-a">История</p>
            <div style={{ fontSize: 12, color: "var(--mnp-fg-2)", lineHeight: 1.8 }}>
              {data.statusLog?.length ? (
                data.statusLog.map((l, i) => (
                  <div key={i}>
                    • {formatDateTime(l.createdAt)} —{" "}
                    {l.oldStatus
                      ? `${FLEX_STATUS_LABELS_RU[l.oldStatus as FlexStatus] ?? l.oldStatus} → `
                      : ""}
                    <span className="bold">
                      {FLEX_STATUS_LABELS_RU[l.newStatus as FlexStatus] ?? l.newStatus}
                    </span>
                  </div>
                ))
              ) : (
                <div className="muted">История пуста</div>
              )}
              <div>• {formatDateTime(data.createdAt)} — заказ создан</div>
            </div>
          </div>
        )}
        {/* Reject-модалка: textarea + кнопки. Останавливает onClick-bubble,
            чтобы клик внутри не закрывал drawer. */}
        {rejectOpen && (
          <div
            data-testid="flex-reject-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15, 23, 42, 0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              padding: 20,
            }}
          >
            <div
              style={{
                background: "var(--mnp-bg, #fff)",
                borderRadius: 12,
                padding: 20,
                width: "min(480px, 100%)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Отклонить заявку</h3>
              <p style={{ color: "var(--mnp-fg-2)", fontSize: 12, marginTop: 0 }}>
                Опишите, что нужно исправить — текст уйдёт клиенту в Telegram.
              </p>
              <textarea
                data-testid="flex-reject-textarea"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={5}
                maxLength={2000}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--mnp-border, #cbd5e1)",
                  fontSize: 13,
                  lineHeight: 1.4,
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
                placeholder="Например: нечитаемые фото паспорта, не указан индекс получателя…"
              />
              {rejectError && (
                <p
                  style={{ color: "var(--mnp-red, #c00)", fontSize: 12, marginTop: 6 }}
                  data-testid="flex-reject-error"
                >
                  {rejectError}
                </p>
              )}
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}
              >
                <button
                  className="btn-admin ghost"
                  onClick={() => setRejectOpen(false)}
                  disabled={reject.isPending}
                >
                  Отменить
                </button>
                <button
                  className="btn-admin primary"
                  onClick={submitReject}
                  disabled={reject.isPending}
                  data-testid="flex-reject-submit"
                >
                  {reject.isPending ? "Отправка…" : "Отклонить и уведомить"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
